import { useEffect, useState } from 'react';
import { api, ApiFailure, type LookerTemplate, type Report } from '../api';
import { Notice } from './Panels';

/**
 * The Looker Studio hand-off.
 *
 * The screen is honest about which of three things is happening, because they
 * are genuinely different products from the user's point of view:
 *
 *   - **Publish and open** — the data goes to a Google Sheet in *their* Drive
 *     and the link creates a report bound to it. Live data, one click.
 *   - **Link an existing source** — they already have the data in Sheets or
 *     BigQuery; we just build the URL.
 *   - **Download the package** — the CSV, the field guide and the chart-by-chart
 *     build sheet, for when neither of the above is available.
 *
 * The predecessor offered one button that produced an empty JSON file and
 * instructions for a menu item Looker Studio does not have.
 */

type Mode = 'publish' | 'link' | 'package';

export function LookerStudio({ report }: { report: Report }) {
  const [template, setTemplate] = useState<LookerTemplate | null>(null);
  const [copiesTemplate, setCopiesTemplate] = useState(false);
  const [mode, setMode] = useState<Mode>('publish');
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  useEffect(() => {
    api
      .lookerTemplate(report.id)
      .then((response) => {
        setTemplate(response.template);
        setCopiesTemplate(response.templateReportConfigured);
      })
      .catch((error: unknown) => setFailure(asFailure(error)));
  }, [report.id]);

  return (
    <div className="page">
      <header className="intro">
        <h2>Looker Studio</h2>
        <p>
          Get this dashboard into your own Looker Studio account. Every field type, aggregation and
          chart below comes from the analysis of your data — nothing here is a fixture.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {!copiesTemplate && (
        <Notice
          message="This deployment creates a report with your data attached, not a pre-built one."
          remedy="Looker Studio has no report import format. To make it one click to a finished dashboard, build the report once by hand, share it as “anyone with the link can view”, and set LOOKER_TEMPLATE_REPORT_ID to its id — every export after that copies it."
        />
      )}

      <div className="segmented" style={{ alignSelf: 'flex-start' }}>
        <button aria-pressed={mode === 'publish'} onClick={() => setMode('publish')}>
          Publish &amp; open
        </button>
        <button aria-pressed={mode === 'link'} onClick={() => setMode('link')}>
          Link existing source
        </button>
        <button aria-pressed={mode === 'package'} onClick={() => setMode('package')}>
          Download package
        </button>
      </div>

      {mode === 'publish' && <PublishPanel report={report} copiesTemplate={copiesTemplate} />}
      {mode === 'link' && <LinkPanel report={report} copiesTemplate={copiesTemplate} />}
      {mode === 'package' && <PackagePanel report={report} />}

      {template !== null && <TemplatePreview template={template} />}
    </div>
  );
}

function PublishPanel({ report, copiesTemplate }: { report: Report; copiesTemplate: boolean }) {
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<'all' | 'sample'>('all');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.lookerPublish>> | null>(null);

  async function publish(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      setResult(await api.lookerPublish(report.id, { credentials, rows }));
    } catch (error) {
      setFailure(asFailure(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <header>
        <h2>Publish to Google Sheets, then open in Looker Studio</h2>
      </header>

      <p className="muted" style={{ marginTop: 0 }}>
        Writes this dataset to a new spreadsheet in your own Drive, then gives you a link that creates
        the Looker Studio report bound to it. The credential is used for this request and never stored.
      </p>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
        <label className="field">
          Service account JSON
          <textarea
            spellCheck={false}
            placeholder='{ "type": "service_account", "client_email": "…", "private_key": "…" }'
            value={credentials['serviceAccountJson'] ?? ''}
            onChange={(e) => setCredentials({ serviceAccountJson: e.target.value })}
          />
          {/* The write scope is a real escalation over the read-only one the
              Sheets connector asks for; say so rather than requesting quietly. */}
          <span className="field-help">
            Needs the <code>https://www.googleapis.com/auth/spreadsheets</code> scope — write access,
            broader than the read-only scope the Sheets connector uses. A service account also needs
            Drive storage, so share a Drive folder with it or use an OAuth refresh token from a real
            account instead.
          </span>
        </label>

        <div className="row">
          <label className="field">
            Rows to publish
            <select value={rows} onChange={(e) => setRows(e.target.value as 'all' | 'sample')}>
              <option value="all">All rows</option>
              <option value="sample">First 5,000</option>
            </select>
          </label>

          <button
            className="primary"
            style={{ alignSelf: 'flex-end' }}
            onClick={publish}
            disabled={busy || (credentials['serviceAccountJson'] ?? '').trim() === ''}
          >
            {busy ? <span className="spinner" /> : 'Publish'}
          </button>
        </div>
      </div>

      {result !== null && (
        <div style={{ marginTop: 16 }}>
          <Notice
            tone="good"
            message={`Published ${result.spreadsheet.rowsWritten.toLocaleString('en-US')} rows.`}
            remedy={result.warnings[0]}
          />

          <div className="row" style={{ marginTop: 12 }}>
            {result.links !== null && (
              <a href={result.links.createReport} target="_blank" rel="noreferrer">
                <button className="primary">
                  {copiesTemplate ? 'Copy the report to my account →' : 'Create the report →'}
                </button>
              </a>
            )}
            <a href={result.spreadsheet.url} target="_blank" rel="noreferrer">
              <button className="ghost">Open the spreadsheet</button>
            </a>
            {result.links?.explore != null && (
              <a href={result.links.explore} target="_blank" rel="noreferrer">
                <button className="ghost">Explore in Looker Studio</button>
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function LinkPanel({ report, copiesTemplate }: { report: Report; copiesTemplate: boolean }) {
  const [connector, setConnector] = useState<'googleSheets' | 'bigQuery'>('googleSheets');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [url, setUrl] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const required =
    connector === 'googleSheets'
      ? [
          { key: 'spreadsheetId', label: 'Spreadsheet id', help: 'The long id in the sheet URL, between /d/ and /edit.' },
          { key: 'worksheetId', label: 'Sheet (tab) id', help: 'The gid in the URL. The first tab is usually 0.' },
        ]
      : [
          { key: 'projectId', label: 'Project id', help: '' },
          { key: 'datasetId', label: 'Dataset id', help: '' },
          { key: 'tableId', label: 'Table id', help: '' },
        ];

  async function build(): Promise<void> {
    setFailure(null);
    try {
      const response = await api.lookerLink(report.id, { connector, ...fields });
      setUrl(response.url);
    } catch (error) {
      setFailure(asFailure(error));
    }
  }

  return (
    <section className="card">
      <header>
        <h2>Link a source you already have</h2>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <div className="row" style={{ marginTop: 4, alignItems: 'flex-end' }}>
        <label className="field">
          Connector
          <select
            value={connector}
            onChange={(e) => {
              setConnector(e.target.value as 'googleSheets' | 'bigQuery');
              setFields({});
              setUrl(null);
            }}
          >
            <option value="googleSheets">Google Sheets</option>
            <option value="bigQuery">BigQuery</option>
          </select>
        </label>

        {required.map((field) => (
          <label className="field grow" key={field.key}>
            {field.label}
            <input
              type="text"
              value={fields[field.key] ?? ''}
              onChange={(e) => setFields({ ...fields, [field.key]: e.target.value })}
            />
            {field.help !== '' && <span className="field-help">{field.help}</span>}
          </label>
        ))}

        <button
          className="primary"
          onClick={build}
          disabled={required.some((f) => (fields[f.key] ?? '').trim() === '')}
        >
          Build link
        </button>
      </div>

      {url !== null && (
        <div style={{ marginTop: 14 }}>
          <a href={url} target="_blank" rel="noreferrer">
            <button className="primary">
              {copiesTemplate ? 'Copy the report to my account →' : 'Create the report →'}
            </button>
          </a>
          <p className="muted" style={{ marginTop: 8, wordBreak: 'break-all' }}>{url}</p>
        </div>
      )}
    </section>
  );
}

function PackagePanel({ report }: { report: Report }) {
  return (
    <section className="card">
      <header>
        <h2>Download the template package</h2>
      </header>

      <p className="muted" style={{ marginTop: 0 }}>
        A zip containing the data as CSV, the field guide (type, aggregation and semantic type per
        column), the chart-by-chart build sheet, the machine-readable template, and a README that walks
        through it. Rebuilding by hand takes about ten minutes.
      </p>

      <div className="row" style={{ marginTop: 12 }}>
        <a href={api.exportUrl(report.id, 'looker-package')} download>
          <button className="primary">Download package (.zip)</button>
        </a>
        <a href={api.exportUrl(report.id, 'looker')} download>
          <button className="ghost">template.json only</button>
        </a>
      </div>
    </section>
  );
}

function TemplatePreview({ template }: { template: LookerTemplate }) {
  const elements = template.pages.flatMap((page) => page.elements);

  return (
    <>
      <section className="card">
        <header>
          <h2>Fields</h2>
          <span className="sub">{template.dataSource.fields.length} columns</span>
        </header>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Concept</th>
                <th>Default aggregation</th>
                <th>Semantic type</th>
                <th className="num">Populated</th>
              </tr>
            </thead>
            <tbody>
              {template.dataSource.fields.map((field) => (
                <tr key={field.id}>
                  <td>{field.name}</td>
                  <td>{field.dataType}</td>
                  <td>
                    <span className={field.conceptType === 'METRIC' ? 'badge accent' : 'badge'}>
                      {field.conceptType}
                    </span>
                  </td>
                  <td>{field.defaultAggregation}</td>
                  <td>{field.semanticType}</td>
                  <td className="num">
                    {/* A sparse column on a scorecard is the quiet way a
                        dashboard starts lying; flag it before they build it. */}
                    <span className={field.completeness < 90 ? 'delta down' : ''}>
                      {field.completeness}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {template.dataSource.calculatedFields.length > 0 && (
        <section className="card">
          <header>
            <h2>Calculated fields to add</h2>
          </header>
          {template.dataSource.calculatedFields.map((field) => (
            <article className="insight" key={field.name}>
              <h3>{field.name}</h3>
              <pre
                style={{
                  margin: '6px 0',
                  padding: '8px 10px',
                  background: 'var(--sunken)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 12.5,
                  overflowX: 'auto',
                }}
              >
                {field.formula}
              </pre>
              <p>{field.why}</p>
            </article>
          ))}
        </section>
      )}

      <section className="card">
        <header>
          <h2>Charts to build</h2>
          <span className="sub">{elements.length} components</span>
        </header>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Chart</th>
                <th>Type</th>
                <th>Dimension</th>
                <th>Metric</th>
                <th>Aggregation</th>
                <th>Sort</th>
              </tr>
            </thead>
            <tbody>
              {elements.map((element) => (
                <tr key={element.id}>
                  <td>{element.title}</td>
                  <td>{element.type}</td>
                  <td>{element.dimension ?? '—'}</td>
                  <td>{element.metric ?? '—'}</td>
                  <td>{element.aggregation}</td>
                  <td>{element.sort ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure('The request failed.', 'Try again.', 0);
}
